import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs';
import { filter, finalize, take } from 'rxjs/operators';

// Services
import {
  GetReservationRequestsGQL,
  GetUpcomingBookingsGQL,
  GetWaitlistBookingsGQL,
  Booking as gqlBooking,
} from '@app/services/graphql.service';
import { AuthService } from '@app/auth/auth.service';
import { LoadingIndicatorService } from '@app/services/loading-indicator.service';

// Models
import { ParsedBooking, Status } from '@app/models/booking.model';

export enum ReservationType {
  Boarding = 'Boarding',
  Interview = 'Interview',
}

@Injectable({
  providedIn: 'root',
})
export class BookingService {
  // #region -> Property <-
  // ----------
  bookingList: ParsedBooking[];
  selectedBooking: ParsedBooking;
  landingBookingList: ParsedBooking[];
  upcomingList$: Observable<ParsedBooking[]>;
  waitlistList$: Observable<ParsedBooking[]>;
  reservationList$: Observable<ParsedBooking[]>;
  upcomingBookingList: ParsedBooking[];

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Constructor <-
  // ----------

  constructor(
    private authService: AuthService,
    private loadingIndicatorService: LoadingIndicatorService,
    private getUpcomingBookingsQuery: GetUpcomingBookingsGQL,
    private getWaitlistBookingQuery: GetWaitlistBookingsGQL,
    private getReservationRequests: GetReservationRequestsGQL
  ) {}

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method <-
  // ----------

  fetchUpcomingBooks(accountId: string) {
    //
    return this.getUpcomingBookingsQuery.fetch({ accountId }).pipe(
      map((res) => {
        // Add to the service

        // Return "sorted"
        return this.convertToBookings(
          res.data.getUpcomingBookings.sort((book1, book2) =>
            book1.startDate > book2.startDate ? 1 : -1
          )
        );
      })
    );
  }

  // populate the booking lists with or without loading indicator
  fetchBookings(showLoadingIndicator: boolean) {
    if (showLoadingIndicator) {
      this.loadingIndicatorService.show();
    }
    this.authService.accountId$
      .pipe(
        filter((accountId) => !!accountId),
        take(1)
      )
      .subscribe((accountId) => {
        this.upcomingList$ = this.getUpcomingBookingsQuery
          .fetch({ accountId })
          .pipe(
            map((res) => {
              this.upcomingBookingList = this.convertToBookings(
                res.data.getUpcomingBookings.sort((book1, book2) =>
                  book1.startDate > book2.startDate ? 1 : -1
                )
              );
              this.landingBookingList = this.upcomingBookingList.filter(
                (booking) => booking.status !== Status.Declined
              );

              // Add to the service
              return this.upcomingBookingList;
            })
          );
        this.waitlistList$ = this.getWaitlistBookingQuery
          .fetch({ accountId })
          .pipe(
            map((res) => {
              // Add to the service

              return this.convertToBookings(res.data.getWaitlistBookings);
            })
          );
        this.reservationList$ = this.getReservationRequests
          .fetch({ accountId })
          .pipe(
            map((res) => {
              // Add to the service
              for (const booking of res.data.getReservationRequests) {
                booking.status = Status.Pending;
              }
              return this.convertToBookings(res.data.getReservationRequests);
            })
          );
        forkJoin([
          this.upcomingList$,
          this.waitlistList$,
          this.reservationList$,
        ])
          .pipe(
            finalize(() => {
              if (showLoadingIndicator) {
                this.loadingIndicatorService.hide();
              }
            })
          )
          .subscribe(([upcomingList, waitlistList, reservationList]) => {
            let records = upcomingList.concat(waitlistList);
            records = records.concat(reservationList);
            this.bookingList = records.sort((book1, book2) =>
              book1.startDate > book2.startDate ? 1 : -1
            );
          });
      });
  }

  // converts from gql booking to booking.modal booking so we can use its methods
  convertToBookings(bookings): ParsedBooking[] {
    const convertedBookings = [];
    for (const booking of bookings) {
      if (booking.categories.includes('Interview')) {
        booking.isInterviewRequest = true;
      }
      convertedBookings.push(new ParsedBooking(booking));
    }
    return convertedBookings;
  }
  // checks if the booking includes a specific category
  includeSpecificType(types: ReservationType[]): boolean {
    //
    if (this.upcomingBookingList?.length > 0) {
      //
      for (const booking of this.upcomingBookingList) {
        for (const type of types) {
          if (booking.categories.includes(type)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  /**
   * Check if the dog has an "interview" related objects from the GQL data.
   *
   * @param bookingList The list of GQL booking objects
   * @returns
   */
  hasInterviewEvent(bookingList: gqlBooking[]): boolean {
    //
    for (const booking of bookingList) {
      if (booking.categories.includes(ReservationType.Interview)) {
        return true;
      }
    }
  }

  // ----------
  // #endregion

  /////////////////////////////
}
